/**
 * Course progression utilities shared between frontend and backend.
 *
 * State evaluation requires linear progression: each item unlocks only after
 * the previous item is complete. Pairing is positional — items are gated
 * against the previous item in the given order.
 */

export interface ProgressionItem {
  id: string;
  required?: boolean;
  minTimeSeconds?: number;
}

/**
 * Sum of configured minTimeSeconds across required items. Does not account for video duration or
 * bypass accounts; the backend's completion gates may require more time than this.
 */
export function requiredMinimumSeconds(items: ProgressionItem[]): number {
  return items
    .filter((item) => item.required)
    .reduce((sum, item) => sum + Math.max(0, item.minTimeSeconds ?? 0), 0);
}

export interface ProgressionProgressRow {
  itemId: string;
  status: string;
}

export interface ItemAccessEntry {
  itemId: string;
  unlocked: boolean;
}

export function completedItemIdSet(progress: Iterable<ProgressionProgressRow>): Set<string> {
  const completed = new Set<string>();
  for (const row of progress) {
    if (row.status === 'completed') completed.add(row.itemId);
  }
  return completed;
}

export function isIndexUnlocked(
  items: ProgressionItem[],
  index: number,
  completedItemIds: ReadonlySet<string>
): boolean {
  if (index <= 0) return true;
  return completedItemIds.has(items[index - 1].id);
}

export function isItemUnlocked(
  items: ProgressionItem[],
  itemId: string,
  completedItemIds: ReadonlySet<string>
): boolean {
  const index = items.findIndex((item) => item.id === itemId);
  if (index < 0) return false;
  return isIndexUnlocked(items, index, completedItemIds);
}

export function itemAccessList(
  items: ProgressionItem[],
  completedItemIds: ReadonlySet<string>,
  allUnlocked = false
): ItemAccessEntry[] {
  return items.map((item, index) => ({
    itemId: item.id,
    unlocked: allUnlocked || isIndexUnlocked(items, index, completedItemIds),
  }));
}

export function requiredCompletionPercent(
  requiredItemIds: string[],
  completedItemIds: ReadonlySet<string>
): number {
  if (requiredItemIds.length === 0) return 0;
  return Math.round(
    (requiredItemIds.filter((id) => completedItemIds.has(id)).length / requiredItemIds.length) * 100
  );
}
